/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Inject, Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { Select } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ACCOUNT_DATA_SERVICE, AccountDataService } from '../../page-modules/account/services/account-data.service';
import { GlobalLaunchHelper } from '../../user-auth/helpers/global-launch.helper';
import { UserAuthState } from '../../user-auth/store/user-auth.state';

@Injectable({
  providedIn: 'root',
})
export class ForceTwoFactorAuthGuard implements CanActivate {
  // Org's own setting on v3, the legacy feature flag on v2 — see UserAuthState.shouldEnforceTwoFactorAuth.
  @Select(UserAuthState.shouldEnforceTwoFactorAuth)
  forceTwoFactorAuthenticationEnabled$: Observable<boolean>;

  constructor(
    private router: Router,
    @Inject(ACCOUNT_DATA_SERVICE) private accountService: AccountDataService,
  ) {}

  canActivate(): Observable<boolean> {
    const redirectExtras = GlobalLaunchHelper.guardRedirectExtras(this.router);
    return this.forceTwoFactorAuthenticationEnabled$.pipe(
      take(1),
      switchMap((forceTwoFactorEnabled) => {
        if (!forceTwoFactorEnabled) {
          return of(true);
        }

        return this.accountService.get2FAStatus().pipe(
          map(({ isSuccess, value }) => {
            if (!isSuccess || !value) {
              this.router.navigate(['/two-factor-setup'], redirectExtras);
              return false;
            }

            if (value.bypass) {
              return true;
            }

            if (value.enabled) {
              return true;
            }

            this.router.navigate(['/two-factor-setup'], redirectExtras);
            return false;
          }),
        );
      }),
    );
  }
}
